import { Fragment, useRef, type CSSProperties, type ReactNode } from "react";
import { bringIntoView } from "../../ui/scroll";
import type { Channel, Menu, Product } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { eurosToCents, formatAmount, formatEuro } from "../../lib/money";
import { activeDay, priceOf } from "../promotions/promotions";
import { categoryColor, productsIn, sellableCategories } from "./menu-view";
import { isDrink, tileCode } from "./quick-entry";

interface Props {
  menu: Menu;
  /** The kind of order: the day promotion (Acties) holds per channel. */
  channel: Channel;
  /** The category shown; null: the first. A code typed at F3 opens the category of its product. */
  category: string | null;
  onCategory: (categoryId: string) => void;
  /** With a size when the tile is product + size (a category shown per size, or a drink). */
  onProduct: (product: Product, variant?: string) => void;
  /** The bar above the categories (prototype menuhead): Code F3, ☎ Test oproep (demo only), Bellerslijst F10, ⚠ Allergenen. */
  head: ReactNode;
}

/** Categories on top, product tiles below: the right half of the sale screen (prototype renderGrid). */
export function MenuPanel({ menu, channel, category: chosen, onCategory, onProduct, head }: Props) {
  const { soldOut, promotions } = useTill();
  const categories = sellableCategories(menu);
  const index = Math.max(
    0,
    categories.findIndex((category) => category.id === chosen),
  );
  const category = categories[index];
  const color = category ? categoryColor(category, index) : undefined;

  const tile = (product: Product, variant?: string) => {
    // sold out (Beheer › Uitverkocht): faded, marked and not tappable, as in the prototype
    const out = soldOut.has(product.id);
    // the day promotion (Acties): the day price on the tile and its label, as in the prototype
    const promo = out ? null : activeDay(promotions, channel, product);
    const amount = (label: string) => eurosToCents(priceOf(promotions, channel, product, label));
    // "€ 2,75" for one price or one size, "11,90 / 13,90 / 16,90" for a product in sizes (prototype)
    const price =
      variant !== undefined
        ? formatEuro(amount(variant))
        : product.variants.length === 1
          ? formatEuro(amount(product.variants[0]!.label))
          : product.variants.map((item) => formatAmount(amount(item.label))).join(" / ");
    const code = tileCode(menu, product, variant);
    return (
      <button
        key={`${product.id}:${variant ?? ""}`}
        type="button"
        className={`tile${out ? " soldout" : ""}`}
        style={{ "--cc": color } as CSSProperties}
        disabled={out}
        // the code as tooltip (prototype: "de code verschijnt als je over een tegel beweegt")
        title={code !== undefined ? t.sale.tileCode(code) : undefined}
        onClick={() => {
          onProduct(product, variant);
        }}
      >
        {promo && <span className="promo">{promo.label}</span>}
        <span className="tn">
          {product.name}
          {variant !== undefined && <small className="sz"> {variant}</small>}
        </span>
        <span className="tp">
          <b className="num">{price}</b>
        </span>
        {out && <span className="out">{t.soldOut.mark}</span>}
      </button>
    );
  };

  const grid = () => {
    if (!category) return null;
    // a product of € 0 made for the action "Gratis product" only comes with the action, never as a tile
    const free = promotions?.free;
    const products = productsIn(menu, category.id).filter(
      (product) => !(free?.productId === product.id && product.variants.every((variant) => variant.price === 0)),
    );
    // "Op de kassa per formaat gegroepeerd" (Menu & prijzen): a section per size, tile = product + size
    if (category.tillSplitBySize === true && products.some((product) => product.variants.length > 1)) {
      const sizes: string[] = [];
      for (const product of products) {
        if (product.variants.length < 2) continue;
        for (const variant of product.variants) if (!sizes.includes(variant.label)) sizes.push(variant.label);
      }
      const single = products.filter((product) => product.variants.length < 2);
      return (
        <>
          {sizes.map((size) => (
            <Fragment key={size}>
              <div className="gsec">
                {category.name} {size.toLowerCase()}
              </div>
              {products
                .filter(
                  (product) =>
                    product.variants.length > 1 && product.variants.some((variant) => variant.label === size),
                )
                .map((product) => tile(product, size))}
            </Fragment>
          ))}
          {single.length > 0 && (
            <>
              <div className="gsec">{t.sale.oneSize(category.name)}</div>
              {single.map((product) => tile(product))}
            </>
          )}
        </>
      );
    }
    // a drink gets a tile per size (prototype: "Coca-Cola 33 cl", "Coca-Cola 1,5 l"), one tap each
    return products.flatMap((product) =>
      isDrink(menu, product) ? product.variants.map((variant) => tile(product, variant.label)) : [tile(product)],
    );
  };

  /**
   * A phone (one column): after a tap on a category the screen glides down to its products, the categories stay
   * above them (Furkan, 18/10/2026). On a till pc they stand side by side and nothing moves.
   */
  const gridRef = useRef<HTMLDivElement>(null);
  const showProducts = () => {
    if (!window.matchMedia("(max-width: 900px)").matches) return;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    requestAnimationFrame(() => {
      if (gridRef.current) bringIntoView(gridRef.current, { block: "start", smooth: !still });
    });
  };

  return (
    <section className="menu" aria-label="Menu">
      <div className="menuhead">{head}</div>
      <div className="cats" role="toolbar" aria-label="Categorieën">
        {categories.map((item, position) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={item.id === category?.id}
            style={{ "--cc": categoryColor(item, position) } as CSSProperties}
            onClick={() => {
              onCategory(item.id);
              showProducts();
            }}
          >
            {item.name}
          </button>
        ))}
      </div>
      <div className="grid" ref={gridRef}>
        {grid()}
      </div>
    </section>
  );
}
